import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { useAuth } from "@/context/AuthContext";
import { pushPendingShortlist } from "@/lib/pendingIntent";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ArrowRight, ExternalLink, Sparkles, Search, ChevronDown, ChevronUp, Bookmark, Check, X } from "lucide-react";
import CostSalaryBars from "@/components/app/CostSalaryBars";
import NCBadge from "@/components/app/NCBadge";
import { readCache, writeCache } from "@/lib/useCachedFetch";
import RowErrorBoundary from "@/components/app/RowErrorBoundary";
import { useShortlistWriter } from "@/lib/useShortlistWriter";

const COMPARE_CACHE_PREFIX = "wua:compare:";
const SHORTLIST_CACHE_PREFIX = "wua:shortlist:";

const POPULAR_SUBJECTS = [
  "Computer Science", "Economics", "Business", "Engineering", "Law",
  "Medicine", "Psychology", "Mathematics", "History", "Physics",
];

const SOURCE_TIER_LABELS = {
  editorial: { label: "Editorial pick", tone: "text-emerald-800 border-emerald-300 bg-emerald-50" },
  verified:  { label: "Verified",       tone: "text-sky-800 border-sky-300 bg-sky-50" },
  directory: { label: "Directory",      tone: "text-stone-700 border-stone-300 bg-stone-50" },
};
// Fallback used when the backend surfaces a new/unknown tier so the UI
// degrades gracefully instead of throwing on `.tone`.
const DEFAULT_TIER_LABEL = SOURCE_TIER_LABELS.directory;


function nativeToUsd(nativeAmount, nativeCcy, rates) {
  if (typeof nativeAmount !== "number") return null;
  if (nativeCcy === "USD" || !rates?.[nativeCcy]) return nativeAmount;
  return nativeAmount / rates[nativeCcy];
}


/**
 * PinButton — one-click add-to-shortlist for a compare-row degree.
 * When unauthenticated we stash the intent locally (via `pushPendingShortlist`)
 * and prompt sign-in; the shortlist finalises on the auth callback.
 */
function PinButton({ degreeId, universityId, universityName, saved, onToggle, size = "md", testid }) {
  const { user, login } = useAuth();
  const { addToShortlist: writeShortlist, removeFromShortlist } = useShortlistWriter();
  const [pending, setPending] = useState(false);
  const isSaved = !!saved;

  const handle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (pending) return;
    if (!user) {
      // Unauth users can only add — sign-in flow finalises on callback.
      // Removing requires a session, so we just prompt sign-in either way.
      pushPendingShortlist(universityId, universityName || universityId, degreeId);
      login();
      return;
    }
    setPending(true);
    try {
      if (isSaved) {
        await removeFromShortlist({ university_id: universityId, degree_id: degreeId });
        onToggle?.(degreeId, false);
        toast.success("Removed from your shortlist.");
      } else {
        await writeShortlist({ university_id: universityId, degree_id: degreeId });
        onToggle?.(degreeId, true);
        toast.success("Added to your shortlist.");
      }
    } catch {
      toast.error(isSaved
        ? "Couldn't remove from shortlist — try again?"
        : "Couldn't add to shortlist — try again?");
    } finally {
      setPending(false);
    }
  };

  const compact = size === "sm";
  return (
    <button
      type="button"
      onClick={handle}
      disabled={pending}
      data-testid={testid || `pin-${degreeId}`}
      data-saved={isSaved ? "true" : "false"}
      title={isSaved ? "On your shortlist — click to remove" : "Add to shortlist"}
      className={`inline-flex items-center justify-center rounded-full border transition-colors group ${
        compact ? "h-6 w-6" : "h-8 w-8"
      } ${
        isSaved
          ? "border-emerald-300 bg-emerald-50 text-emerald-800 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-700"
          : "border-stone-300 bg-white text-stone-700 hover:border-[#4C1D95] hover:text-[#4C1D95]"
      } ${pending ? "opacity-60 cursor-wait" : ""}`}
    >
      {isSaved ? (
        <>
          <Check className="h-3.5 w-3.5 group-hover:hidden" />
          <X className="h-3.5 w-3.5 hidden group-hover:block" />
        </>
      ) : (
        <Bookmark className="h-3.5 w-3.5" />
      )}
      <span className="sr-only">{isSaved ? "Remove from shortlist" : "Add to shortlist"}</span>
    </button>
  );
}


function StatTile({ label, value, sublabel, testid }) {
  return (
    <div className="border-t border-stone-100 pt-3" data-testid={testid}>
      <div className="label-uppercase !text-[9px] text-stone-500">{label}</div>
      <div className="font-editorial text-2xl mt-0.5 leading-tight text-stone-900">{value ?? "—"}</div>
      {sublabel && <div className="mt-0.5 text-[10px] font-underground-sc tracking-wider text-stone-500">{sublabel}</div>}
    </div>
  );
}


function AlternateRow({ alt, formatCurrency, rates, country, saved, onToggle }) {
  const salUsd = nativeToUsd(alt.salary_median_native, alt.salary_median_currency, rates);
  return (
    <li
      className="flex items-baseline justify-between gap-3 py-1.5"
      data-testid={`cross-country-alt-${country}-${alt.degree_id}`}
    >
      <div className="min-w-0 flex-1">
        <Link
          to={`/degree/${alt.degree_id}`}
          className="text-xs font-medium text-stone-900 hover:text-[#4C1D95] block truncate"
        >
          {alt.official_title}
        </Link>
        <div className="text-[10px] font-underground-sc tracking-wider text-stone-500 truncate">
          {alt.university_name}
          {alt.world_rank && <span> · #{alt.world_rank}</span>}
          {alt.source_tier === "editorial" && <span className="text-emerald-700 ml-1">· ed.</span>}
        </div>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <div className="text-[11px] font-mono text-stone-700">
          {salUsd ? formatCurrency(salUsd) : "—"}
        </div>
        <PinButton
          degreeId={alt.degree_id}
          universityId={alt.university_id}
          universityName={alt.university_name}
          saved={saved}
          onToggle={onToggle}
          size="sm"
          testid={`pin-alt-${country}-${alt.degree_id}`}
        />
      </div>
    </li>
  );
}


function CountryColumn({ row, formatCurrency, rates, savedIds, onToggle, sharedMaxUsd }) {
  const [showAlt, setShowAlt] = useState(false);
  const t = row.top_degree;
  const tier = SOURCE_TIER_LABELS[t?.source_tier] || DEFAULT_TIER_LABEL;
  const salaryUsd = t ? nativeToUsd(t.salary_median_native, t.salary_median_currency, rates) : null;
  const y3Usd = t ? nativeToUsd(t.salary_after_progression_native, t.salary_after_progression_currency, rates) : null;
  const growthPct = salaryUsd && y3Usd && y3Usd > salaryUsd
    ? Math.round(((y3Usd - salaryUsd) / salaryUsd) * 100)
    : null;
  const alts = row.alternates || [];
  const moreCount = Math.max(row.alternates_count - alts.length, 0);

  return (
    <Card className="p-5 border-stone-200 bg-white flex flex-col" data-testid={`cross-country-col-${row.country}`}>
      <div className="flex items-center gap-2">
        <span className="text-2xl" aria-hidden="true">{row.flag}</span>
        <div className="label-uppercase !text-[10px] text-stone-500">{row.country}</div>
        {t && (
          <span className={`ml-auto inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[9px] font-underground-sc tracking-wider ${tier.tone}`}>
            {t.source_tier === "editorial" && <Sparkles className="h-2.5 w-2.5" />}
            {tier.label}
          </span>
        )}
      </div>
      {t?.degree_warnings && (
        <div className="mt-2">
          <NCBadge
            warnings={t.degree_warnings}
            testid={`nc-badge-compare-${row.country}`}
          />
        </div>
      )}

      {!t ? (
        <p className="mt-4 text-sm text-stone-500 italic">
          No {row.country} match for this subject yet — the directory pipeline hasn't ingested a suitable course.
        </p>
      ) : (
        <>
          <div className="mt-3">
            <h3 className="font-editorial text-xl leading-tight">
              <Link
                to={`/degree/${t.degree_id}`}
                data-testid={`cross-country-degree-${row.country}`}
                className="hover:underline underline-offset-4"
              >
                {t.official_title}
              </Link>
            </h3>
            <div className="mt-1 text-xs text-stone-600 flex items-center gap-1">
              <Link to={`/uni/${t.university_id}`} className="hover:underline truncate">{t.university_name}</Link>
              {t.university_city && <span className="text-stone-400"> · {t.university_city}</span>}
              {t.world_rank && <span className="text-stone-400"> · #{t.world_rank}</span>}
            </div>
            <div className="mt-1 text-[10px] font-underground-sc tracking-wider text-stone-500">
              {t.qualification} · {t.duration_years}Y {row.alternates_count > 0 && `· ${row.alternates_count} alternative${row.alternates_count === 1 ? "" : "s"} in country`}
            </div>
          </div>

          <div className="mt-5 space-y-3 flex-1">
            <StatTile
              label="Median starting salary"
              value={salaryUsd ? formatCurrency(salaryUsd) : "Not published"}
              sublabel={growthPct ? `→ ${formatCurrency(y3Usd)} @ 3y (+${growthPct}%)` : null}
              testid={`stat-salary-${row.country}`}
            />
            <StatTile
              label="Intl tuition / year"
              value={t.cost_intl_annual_usd ? formatCurrency(t.cost_intl_annual_usd) : "Not published"}
              sublabel={t.cost_intl_annual_usd ? `${formatCurrency(t.cost_intl_annual_usd * (t.duration_years || 3))} full degree` : null}
              testid={`stat-cost-${row.country}`}
            />
            <StatTile
              label="Post-study work visa"
              value={t.graduate_visa_years ? `${t.graduate_visa_years}-year graduate route` : "Check locally"}
              testid={`stat-visa-${row.country}`}
            />
            <CostSalaryBars
              costUsd={t.cost_intl_annual_usd ? t.cost_intl_annual_usd * (t.duration_years || 3) : null}
              salaryUsd={salaryUsd}
              maxUsd={sharedMaxUsd}
              formatCurrency={formatCurrency}
              testid={`cost-salary-bars-${row.country}`}
            />
          </div>

          <div className="mt-4 pt-3 border-t border-stone-100 flex items-center gap-2">
            <Link
              to={`/degree/${t.degree_id}`}
              data-testid={`cross-country-cta-${row.country}`}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-[#4C1D95] hover:text-[#3B0764]"
            >
              See course in detail <ArrowRight className="h-3.5 w-3.5" />
            </Link>
            <div className="ml-auto">
              <PinButton
                degreeId={t.degree_id}
                universityId={t.university_id}
                universityName={t.university_name}
                saved={savedIds.has(t.degree_id)}
                onToggle={onToggle}
                testid={`pin-top-${row.country}`}
              />
            </div>
          </div>

          {alts.length > 0 && (
            <div className="mt-3 pt-3 border-t border-stone-100">
              <button
                type="button"
                onClick={() => setShowAlt((s) => !s)}
                data-testid={`cross-country-alts-toggle-${row.country}`}
                aria-expanded={showAlt}
                className="w-full inline-flex items-center justify-between text-[10px] font-underground-sc tracking-wider text-stone-600 hover:text-stone-900"
              >
                <span>Also considering ({alts.length}{moreCount ? `, +${moreCount} more` : ""})</span>
                {showAlt ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              </button>
              {showAlt && (
                <ul
                  className="mt-2 divide-y divide-stone-100"
                  data-testid={`cross-country-alts-list-${row.country}`}
                >
                  {alts.map((a) => (
                    <AlternateRow
                      key={a.degree_id}
                      alt={a}
                      formatCurrency={formatCurrency}
                      rates={rates}
                      country={row.country}
                      saved={savedIds.has(a.degree_id)}
                      onToggle={onToggle}
                    />
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}
    </Card>
  );
}


export default function CrossCountryCompare() {
  const [params, setParams] = useSearchParams();
  const [subject, setSubject] = useState(params.get("subject") || "Computer Science");
  const [query, setQuery] = useState(params.get("subject") || "Computer Science");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savedIds, setSavedIds] = useState(new Set());
  const { format, rates } = useCurrency();
  const { user } = useAuth();

  const load = async (s, { force = false } = {}) => {
    const key = `${COMPARE_CACHE_PREFIX}${s}`;
    if (!force) {
      const cached = readCache(key);
      if (cached) { setData(cached); return; }
    }
    setLoading(true);
    try {
      const { data } = await api.get("/explore/cross-country", { params: { subject: s, alternates: 3 } });
      setData(data);
      writeCache(key, data);
    } catch {
      setData({ subject: s, results: [] });
    } finally {
      setLoading(false);
    }
  };

  // Hydrate the pin state from the user's existing shortlist so pins that
  // are already saved render as "Pinned" on load.
  useEffect(() => {
    if (!user) { setSavedIds(new Set()); return; }
    let cancelled = false;
    const shortlistKey = `${SHORTLIST_CACHE_PREFIX}${user.user_id || user.email}`;
    (async () => {
      // Serve pinned-state instantly from the shared shortlist cache — pin
      // mutations already bust this prefix so we never render stale ticks.
      const cached = readCache(shortlistKey);
      const buildIds = (raw) => new Set(
        (raw.items || raw || []).flatMap((entry) =>
          (entry.degree_ids || (entry.degree_id ? [entry.degree_id] : [])),
        ),
      );
      if (cached) { setSavedIds(buildIds(cached)); return; }
      try {
        const { data } = await api.get("/shortlist");
        if (cancelled) return;
        setSavedIds(buildIds(data));
        writeCache(shortlistKey, data.items || data);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const handleToggle = (degreeId, nowSaved) => {
    setSavedIds((prev) => {
      const n = new Set(prev);
      if (nowSaved) n.add(degreeId);
      else n.delete(degreeId);
      return n;
    });
  };

  useEffect(() => { load(subject); }, []);   // eslint-disable-line

  const runSearch = (nextSubject) => {
    setSubject(nextSubject);
    setQuery(nextSubject);
    const next = new URLSearchParams(params);
    next.set("subject", nextSubject);
    setParams(next, { replace: true });
    load(nextSubject);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <div className="label-uppercase">Explore · Cross-country compare</div>
      <h1 className="font-editorial text-4xl mt-2 leading-tight">
        How does <em className="text-[#4C1D95]">{subject}</em> stack up across countries?
      </h1>
      <p className="mt-3 text-stone-600 max-w-2xl">
        One representative course per country — editorial pick where we have one, top-outcome directory
        fallback otherwise. All salaries and costs shown in your chosen currency.
      </p>

      <form
        onSubmit={(e) => { e.preventDefault(); runSearch(query || "Computer Science"); }}
        className="mt-6 flex items-center gap-2 max-w-xl"
        data-testid="cross-country-search-form"
      >
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Enter a subject — e.g. Computer Science, Law, Medicine…"
            className="pl-9"
            data-testid="cross-country-search-input"
          />
        </div>
        <Button
          type="submit"
          data-testid="cross-country-search-submit"
          className="bg-[#4C1D95] hover:bg-[#3B0764] text-white"
        >
          Compare
        </Button>
      </form>

      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <span className="text-[11px] font-underground-sc tracking-wider text-stone-500">Popular:</span>
        {POPULAR_SUBJECTS.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => runSearch(s)}
            data-testid={`cross-country-preset-${s.toLowerCase().replace(/\s+/g, "-")}`}
            className={`text-xs font-mono px-2.5 py-1 rounded-full border transition-colors ${
              subject === s
                ? "border-[#4C1D95] bg-[#4C1D95] text-white"
                : "border-stone-300 bg-white text-stone-700 hover:border-stone-400"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {loading || !data ? null : (() => {
        // Compute a shared max so the cost-vs-salary bars are comparable
        // across all five country tiles.
        const sharedMaxUsd = Math.max(
          ...data.results.flatMap((r) => {
            const t = r.top_degree;
            if (!t) return [];
            const cost = t.cost_intl_annual_usd ? t.cost_intl_annual_usd * (t.duration_years || 3) : 0;
            const salUsd = nativeToUsd(t.salary_median_native, t.salary_median_currency, rates) || 0;
            return [cost, salUsd];
          }),
          1,
        );
        return (
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4" data-testid="cross-country-grid">
            {data.results.map((row) => (
              <RowErrorBoundary key={row.country} label={`cross-country-${row.country}`}>
                <CountryColumn
                  row={row}
                  formatCurrency={format}
                  rates={rates}
                  savedIds={savedIds}
                  onToggle={handleToggle}
                  sharedMaxUsd={sharedMaxUsd}
                />
              </RowErrorBoundary>
            ))}
          </div>
        );
      })()}

      <p className="mt-10 text-[11px] font-underground-sc tracking-wider text-stone-500">
        Currency conversions use live rates from `/api/currency/rates`. Cost figures shown for international
        applicants; local nationals typically pay materially less. Salary figures show median starting salary
        (Y1) with 3-year progression where the source publishes it.
      </p>
    </div>
  );
}
